import { debounceTime } from 'rxjs';
import { distinctUntilChanged, map, take } from 'rxjs/operators';
import { ChangeDetectionStrategy, Component, ElementRef, ViewChild } from "@angular/core";
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from "@ngx-translate/core";
import type { MenuItem } from "primeng/api";
import { DialogService } from "primeng/dynamicdialog";
import * as FlatActions from "@medsurf/flat-actions";
import * as FlatModels from '@medsurf/flat-models';
import * as FlatFacades from '@medsurf/flat-facades';
import { ModalControlComponent } from '@medsurf/flat-components';
import { animations } from './slide.animations';

@Component({
    selector: 'medsurf-slide',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './slide.component.html',
    styleUrls: ['./slide.component.scss'],
    animations: animations,
    standalone: false
})
export class SlideComponent {
  @ViewChild('slide_tree_scroll_panel', {static: false}) slideTreeScrollPanel: ElementRef<HTMLDivElement>;
  
  position: number;
  #typedSlideAnnotationsExpanded: { [id: string]: boolean } = {};
  #typedImageAnnotationExpanded: { [id: string]: boolean } = {};
  editMenuItems$ = this.slideAuthorFacade.editPanelOverlay$.pipe(map((editPanelOverlay) => {
    if (editPanelOverlay) {
      return [
        {
          label: this.translate.instant('global.edit_panel_split_screen'),
          icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.TABLE_COLUMNS,
          command: () => this.setEditPanelOverlay(false),
        },
      ] satisfies MenuItem[];
    } else {
      return [
        {
          label: this.translate.instant('global.edit_panel_single'),
          icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.RECTANGLE_VERTICAL_HISTORY,
          command: () => this.setEditPanelOverlay(true),
        },
      ] satisfies MenuItem[];
    }
  }));
  getSlidePasteMenuItems = () => {
    return [
      {
        label: this.translate.instant('global.annotation_paste'),
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.PASTE,
        command: () => this.pasteSelection(),
      },
      {
        label: this.translate.instant('global.annotation_paste_position'),
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.PASTE,
        command: () => this.pasteAnnotationWithPosition(),
      }
    ] satisfies MenuItem[];
  }
  readonly FlatModels = FlatModels;

  constructor(public appSharedFacade: FlatFacades.AppSharedFacade,
              public alertControlFacade: FlatFacades.AlertControlFacade,
              public slideAuthorFacade: FlatFacades.SlideAuthorFacade,
              public nodeSharedFacade: FlatFacades.NodeSharedFacade,
              public bulkEditAuthorFacade: FlatFacades.BulkEditAuthorFacade,
              public dialogService: DialogService,
              public translate: TranslateService) {
    // Load Slide Data By Page
    this.slideAuthorFacade.currentSelectedSlideNode$.pipe(
      takeUntilDestroyed(),
      distinctUntilChanged((a, b) => a?.id === b?.id),
      debounceTime(20)
    ).subscribe((slide) => {
      if (slide) {
        this.slideAuthorFacade.requestGetSlideDataByPageId(slide.page as unknown as string).subscribe({
          error: (result) => this.alertControlFacade.requestAddError(result),
        });
        this.slideAuthorFacade.requestSetImageIndex(0, 0).subscribe({
          error: (result) => this.alertControlFacade.requestAddError(result),
          next: () => {
            if (this.slideAuthorFacade.snapshot_editPanelOverlay()) {
              this.slideAuthorFacade.requestSetEditPanelDisplay(FlatModels.SlideAuthorModels.EditPanelDisplay.enum.list).subscribe({
                error: (result) => this.alertControlFacade.requestAddError(result),
              });
            } else {
              this.slideAuthorFacade.requestSetEditPanelDisplay(FlatModels.SlideAuthorModels.EditPanelDisplay.enum.image).subscribe({
                error: (result) => this.alertControlFacade.requestAddError(result),
              });
            }
            this.slideAuthorFacade.requestAddTypedSlideLayersExpanded(slide.page).subscribe({
              error: (result) => this.alertControlFacade.requestAddError(result),
              next: () => {
                this.slideAuthorFacade.currentSelectedSlideLayer$.pipe(distinctUntilChanged(), take(2)).subscribe((layer) => {
                  if (layer) {
                    this.slideAuthorFacade.requestAddTypedLayerExpanded(layer.id).subscribe({
                      error: (result) => this.alertControlFacade.requestAddError(result),
                    })
                  }
                });
                this.slideAuthorFacade.currentSelectedSlideImage$.pipe(distinctUntilChanged(), take(2)).subscribe((image) => {
                  if (image) {
                    return this.slideAuthorFacade.requestAddTypedImageExpanded(image.id).subscribe({
                      error: (result) => this.alertControlFacade.requestAddError(result),
                    })
                  }
                });
              }
            });
          }
        })
      }
    });
    // Typed Slide Annotations Expanded
    this.slideAuthorFacade.typedSlideAnnotationsExpanded$.pipe(takeUntilDestroyed()).subscribe((typedSlideAnnotationsExpanded) => {
      this.#typedSlideAnnotationsExpanded = typedSlideAnnotationsExpanded;
    });
    // Typed Image Annotation Expanded
    this.slideAuthorFacade.typedImageAnnotationExpanded$.pipe(takeUntilDestroyed()).subscribe((typedImageAnnotationExpanded) => {
      this.#typedImageAnnotationExpanded = typedImageAnnotationExpanded;
    });
    // Scroll Into View The Given Node In The Node Tree
    this.slideAuthorFacade.ofActionDispatchedScrollSlideElementIntoView.pipe(takeUntilDestroyed(), debounceTime(200)).subscribe((data: { action: FlatActions.SlideAuthorActions.ScrollSlideTreeElementIntoView }) => {
      const element = document.getElementById('se-' + data.action.id);
      if (!element || !this.slideTreeScrollPanel) return;
      this.slideTreeScrollPanel.nativeElement.scrollTop = element.offsetTop - element.offsetHeight * 3;
    });
  }
  
  deselectSelection() {
    // Request Deselect Selection
    this.bulkEditAuthorFacade.requestDeselectSelection().subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
  }
  
  copySelection() {
    // Request Copy Selection
    this.bulkEditAuthorFacade.requestCopySelection().subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
  }
  
  pasteSelection() {
    // Request Paste Selection
    this.bulkEditAuthorFacade.requestPasteSelection().subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
  }
  
  pasteAnnotationWithPosition() {
    const pageClone = structuredClone(this.slideAuthorFacade.snapshot_page()) as FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training;
    const imageClone = structuredClone(this.slideAuthorFacade.snapshot_image());
    if (!pageClone || !imageClone) {
      return;
    }
    
    // Request Paste Annotation
    this.slideAuthorFacade.requestPasteAnnotationWithPosition(pageClone, imageClone).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
      next: () => {
        if (imageClone) {
          this.#typedImageAnnotationExpanded[imageClone.id] = true;
          this.slideAuthorFacade.requestSetTypedImageAnnotationExpanded(this.#typedImageAnnotationExpanded).subscribe({
            error: (result) => this.alertControlFacade.requestAddError(result),
          });
        } else {
          this.#typedSlideAnnotationsExpanded[pageClone.id] = true;
          this.slideAuthorFacade.requestSetTypedSlideAnnotationsExpanded(this.#typedSlideAnnotationsExpanded).subscribe({
            error: (result) => this.alertControlFacade.requestAddError(result),
          });
        }
      }
    });
  }
  
  removeSelection() {
    // Show Dialog
    const dialogRef = this.dialogService.open(ModalControlComponent, {
      header: this.translate.instant('global.selection_remove'),
      data: {
        label: 'global.selection_remove_confirm',
        inputLabel: 'global.remove',
        inputClass: FlatModels.ButtonDesignModels.ButtonDesignClass.DANGER
      },
      dismissableMask: true,
      width: '50%'
    });
    dialogRef.onClose.pipe(take(1)).subscribe(remove => {
      if (remove) {
        // Request Remove Selection
        this.bulkEditAuthorFacade.requestRemoveSelection().subscribe({
          error: (result) => this.alertControlFacade.requestAddError(result),
        });
      }
    });
  }
  
  setEditPanelOverlay(overlay = false) {
    this.slideAuthorFacade.requestSetEditPanelOverlay(overlay).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
  }
  
  layerOnResizeStart() {
    this.slideAuthorFacade.requestCollapseLayer(false, true).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
  }
  
  layerOnResizeEnd(event: { sizes: number[] }) {
    this.slideAuthorFacade.requestSetLayerPanelSize(event.sizes).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
  }
  
  editOnResizeStart() {
    this.slideAuthorFacade.requestSetIsSplitterMoving(true).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
    this.slideAuthorFacade.requestCollapseEdit(false, true).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
  }
  
  editOnResizeEnd(event: { sizes: number[] }) {
    this.slideAuthorFacade.requestSetIsSplitterMoving(false).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
    this.slideAuthorFacade.requestSetEditPanelSize(event.sizes).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
  }

  editCollapsedChange(editIsMinimized: boolean) {
    this.slideAuthorFacade.requestCollapseEdit(editIsMinimized).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
  }
}
